import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Trophy, Gamepad2, Dices, Rocket, Spade, Fish, Joystick, Star,
  Heart, Search, SlidersHorizontal, ArrowDownAZ, Inbox, ChevronDown, ChevronUp, X, RefreshCw,
  Info, ShieldCheck, ArrowRightLeft,
} from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { useGameLaunch } from "../hooks/useGameLaunch";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const CONFIG = {
  sports:    { title: "Sports",    icon: <Trophy size={22} />,    accent: "#ffc933" },
  casino:    { title: "Casino",    icon: <Gamepad2 size={22} />,  accent: "#7c3aed" },
  slots:     { title: "Slots",     icon: <Dices size={22} />,     accent: "#ef4444" },
  crash:     { title: "Crash",     icon: <Rocket size={22} />,    accent: "#06b6d4" },
  table:     { title: "Table",     icon: <Spade size={22} />,     accent: "#22c55e" },
  fishing:   { title: "Fishing",   icon: <Fish size={22} />,      accent: "#0ea5e9" },
  arcade:    { title: "Arcade",    icon: <Joystick size={22} />,  accent: "#f59e0b" },
  lottery:   { title: "Lottery",   icon: <Star size={22} />,      accent: "#ec4899" },
  favourite: { title: "Favourite", icon: <Heart size={22} />,     accent: "#c89b3c" },
};

// baji.live CDN icons (colourful illustrated menu icons)
const CDN = "https://img.b112j.com/bj/h5/assets";
const menuIcon = (name) => `${CDN}/v3/images/icon-set/menu-type/active/icon-${name}.png`;

// Inline gold star kept for game-card badge usage (no longer used in dropdown).
const FavoritesIcon = () => null; // placeholder kept to avoid refactor ripple

// Dropdown list — only category entries (Arcade replaced by Favourite per user request).
const DROPDOWN_ITEMS = [
  { slug: "casino",          label: "Casino",          icon: menuIcon("casino"),   to: "/casino" },
  { slug: "slots",           label: "Slots",           icon: menuIcon("slot"),     to: "/slots" },
  { slug: "crash",           label: "Crash",           icon: menuIcon("crash"),    to: "/crash" },
  { slug: "table",           label: "Table",           icon: menuIcon("table"),    to: "/table" },
  { slug: "fishing",         label: "Fishing",         icon: menuIcon("fish"),     to: "/fishing" },
  { slug: "favourite",       label: "Favourite",       iconNode: <Heart size={28} fill="#c89b3c" stroke="#c89b3c" strokeWidth={1.5} />, to: "/favourite" },
  { slug: "lottery",         label: "Lottery",         icon: menuIcon("lottery"),  to: "/lottery" },
];

// Order + short labels used elsewhere.
const CATEGORY_ORDER = ["sports", "casino", "slots", "crash", "table", "fishing", "favourite", "lottery"];
const SHORT_LABEL = {
  sports: "Sports", casino: "Casino", slots: "Slots", crash: "Crash",
  table: "Table", fishing: "Fishing", favourite: "Favourite", lottery: "Lottery",
};

// Alternate keys used for the `type` field (case-insensitive matching).
const TYPE_ALIASES = {
  casino:  ["casino", "live casino", "live"],
  slots:   ["slots", "slot"],
  sports:  ["sports", "sport"],
  crash:   ["crash"],
  table:   ["table"],
  fishing: ["fishing", "fish"],
  arcade:  ["arcade"],
  lottery: ["lottery"],
};

function GameCard({ game, accent, onLaunch, isFavourite, onToggleFavourite }) {
  const [imgOk, setImgOk] = useState(Boolean(game.image));
  useEffect(() => { setImgOk(Boolean(game.image)); }, [game.image]);
  const showFallback = !game.image || !imgOk;
  return (
    <button
      type="button"
      onClick={() => onLaunch && onLaunch(game.id, game.name, game.provider)}
      data-testid={`game-${game.id}`}
      className="group relative rounded-xl overflow-hidden bg-[#161e2a] border border-white/5 hover:border-white/20 focus:outline-none focus:border-white/30 transition-all text-left"
    >
      <div
        className="aspect-[4/5] relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${accent}22 0%, #0a1119 100%)` }}
      >
        {showFallback && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-2 text-center">
            <span
              className="font-display text-5xl sm:text-6xl leading-none"
              style={{ color: `${accent}cc`, letterSpacing: "-0.06em" }}
            >
              {(game.name || "G").slice(0, 2).toUpperCase()}
            </span>
            <span className="text-[11px] sm:text-[12px] font-semibold text-white/75 line-clamp-2 leading-tight">
              {game.name}
            </span>
          </div>
        )}
        {game.image && imgOk && (
          <img
            src={game.image}
            alt={game.name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImgOk(false)}
          />
        )}

        {/* Optional badge — top-left so it's instantly visible (mobile + desktop) */}
        {game.badge && (
          <span
            className="absolute top-2 left-2 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shadow-md z-10"
            style={{
              background: /hot/i.test(game.badge) ? "#ef4444" : /new/i.test(game.badge) ? "#19a55c" : "#f59e0b",
              color: "white",
            }}
          >
            {game.badge}
          </span>
        )}

        {/* Favourite heart — top-right corner. Stops event propagation so tap
            doesn't also launch the game. Brand-gold (#c89b3c) when active. */}
        {onToggleFavourite && (
          <span
            role="button"
            tabIndex={0}
            data-testid={`fav-${game.id}`}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onToggleFavourite(game.id); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation(); e.preventDefault(); onToggleFavourite(game.id);
              }
            }}
            aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
            className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 h-8 w-8 sm:h-9 sm:w-9 rounded-full grid place-items-center bg-black/55 hover:bg-black/75 backdrop-blur-sm transition-transform active:scale-90 cursor-pointer z-10"
            style={{ boxShadow: "0 4px 10px rgba(0,0,0,0.5)" }}
          >
            <Heart
              size={16}
              fill={isFavourite ? "#c89b3c" : "transparent"}
              stroke={isFavourite ? "#c89b3c" : "#fff"}
              strokeWidth={2}
            />
          </span>
        )}
      </div>
    </button>
  );
}

export default function CategoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const category = location.pathname.replace(/^\//, "").split("/")[0];
  const cfg = CONFIG[category];

  const [games, setGames] = useState(null); // null = loading
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("order"); // order | name | newest
  const [selectedProviders, setSelectedProviders] = useState([]); // empty = all
  const [filterOpen, setFilterOpen] = useState(false);
  const [catMenuOpen, setCatMenuOpen] = useState(false);
  // Pagination — initial 40, +40 per Load more click
  const PAGE_SIZE = 40;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { launchGame, modal: launchModal } = useGameLaunch();

  // ── Favourites (per-user localStorage; shared key with HomeGameGrid) ──
  const userId = user?.id || user?._id || user?.username || null;
  const favKey = (id) => `ml_favourites_${id || "guest"}`;
  const [favourites, setFavourites] = useState(() => {
    try {
      const raw = localStorage.getItem(favKey(userId));
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch { return []; }
  });
  useEffect(() => {
    try {
      const raw = localStorage.getItem(favKey(userId));
      const arr = raw ? JSON.parse(raw) : [];
      setFavourites(Array.isArray(arr) ? arr : []);
    } catch { setFavourites([]); }
  }, [userId]);

  const toggleFavourite = (gameId) => {
    if (!user) {
      toast.info("Please log in to save favourites");
      navigate("/login");
      return;
    }
    setFavourites((prev) => {
      const has = prev.includes(gameId);
      const next = has ? prev.filter((id) => id !== gameId) : [...prev, gameId];
      try { localStorage.setItem(favKey(userId), JSON.stringify(next)); } catch { /* ignore */ }
      toast.success(has ? "Removed from favourites" : "Added to favourites");
      return next;
    });
  };

  // Favourite category: redirect to login if not authenticated.
  useEffect(() => {
    if (category === "favourite" && !user) {
      toast.info("Please log in to view your favourites");
      navigate("/login");
    }
  }, [category, user, navigate]);

  // Tracks last fetch error so we can show a retry banner instead of an
  // endless skeleton spinner. `null` = no error yet (loading or success),
  // string = error message that should be shown to the user.
  const [loadError, setLoadError] = useState(null);
  const reloadTickRef = useRef(0);

  useEffect(() => {
    if (!cfg) return;
    // Skip the fetch when /favourite is opened by a guest — the redirect
    // effect above will send them to /login on the next tick.
    if (category === "favourite" && !user) return;
    let cancelled = false;
    // AbortController so a stuck fetch (slow upstream / flaky mobile network)
    // can be cancelled instead of leaving the page frozen on the skeleton
    // spinner forever. We also auto-retry on failure with a short backoff so
    // transient blips heal themselves without the user having to refresh.
    const ctrl = new AbortController();
    const timeoutMs = 12000;
    const timeoutId = setTimeout(() => {
      try { ctrl.abort(); } catch { /* ignore */ }
    }, timeoutMs);

    setGames(null);
    setLoadError(null);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BACKEND_URL}/api/public/games?_t=${Date.now()}`, {
      cache: "no-store",
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((all) => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        setGames(Array.isArray(all) ? all : []);
        setLoadError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        // Don't blank out the existing games on retry failure — only show
        // an error/empty state when we have NOTHING to render yet.
        setGames((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : []));
        setLoadError(
          err?.name === "AbortError"
            ? "Slow connection — tap retry"
            : "Couldn't load games — tap retry"
        );
      });
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      try { ctrl.abort(); } catch { /* ignore */ }
    };
    // `reloadTickRef.current` is included via `reloadTick` state below to
    // make manual & visibility-driven retries re-run this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, cfg, user, reloadTickRef.current]);

  // Force a re-fetch by bumping the tick counter — used by the retry button
  // and by the visibility/focus listener below.
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => { reloadTickRef.current = reloadTick; }, [reloadTick]);
  const retryLoad = useCallback(() => setReloadTick((n) => n + 1), []);

  // Auto-refresh policy (per user request):
  // We DO NOT auto-refresh on tab focus / visibility change anymore — that was
  // causing the Slots / Casino / Sport pages to silently re-fetch every time
  // the player tabbed back to the site, which wiped their scroll position,
  // search filters, and provider selection. Now the page state persists for
  // the entire session; if the player wants fresh data they can use the
  // retry button or reload manually. The `online` event (network reconnect
  // after a real outage) is the only auto-trigger we keep — that one is
  // strictly recovery, not periodic.
  useEffect(() => {
    if (!cfg) return;
    const onOnline = () => setReloadTick((n) => n + 1);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("online", onOnline);
    };
  }, [cfg]);

  // Reset provider filter when category changes. If the URL carries a
  // `?provider=<name>` query (set by SideMenu sub-category clicks), defer
  // applying it until the games (and therefore the real provider list) load —
  // see the next effect below.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get("provider")) setSelectedProviders([]);
  }, [category, location.search]);

  // Reset visible page whenever the underlying list changes
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [category, search, sort, selectedProviders]);

  // Games narrowed to the current category (or favourites set) — shared
  // by `providerList` and `visible` so the provider filter dropdown only
  // ever shows providers that exist in this category.
  const categoryGames = useMemo(() => {
    if (!games) return null;
    if (category === "favourite") {
      const favSet = new Set(favourites);
      return games.filter((g) => favSet.has(g.id));
    }
    const cat = String(category || "").toLowerCase();
    const aliases = (TYPE_ALIASES[category] || [category]).map((s) => s.toLowerCase());
    return games.filter((g) => {
      const cats = (g.categories || []).map((c) => String(c || "").toLowerCase());
      if (cats.includes(cat)) return true;
      return aliases.includes(String(g.type || "").toLowerCase());
    });
  }, [games, category, favourites]);

  // Unique provider list (sorted, from current category games)
  const providerList = useMemo(() => {
    if (!categoryGames) return [];
    const seen = new Set();
    const list = [];
    for (const g of categoryGames) {
      const p = (g.provider || "").trim();
      if (p && !seen.has(p.toLowerCase())) {
        seen.add(p.toLowerCase());
        list.push(p);
      }
    }
    return list.sort((a, b) => a.localeCompare(b));
  }, [categoryGames]);

  // Apply ?provider=<name> from the URL once we know the actual provider list
  // for this category. Case-insensitive: accepts exact OR substring matches
  // (so "Pragmatic" → "Pragmatic Play", "JILI" → "JILI", etc.). Falls back to
  // the requested name verbatim so the filter chip still appears even if that
  // provider has no games seeded yet.
  useEffect(() => {
    if (games === null) return;
    const params = new URLSearchParams(location.search);
    const want = (params.get("provider") || "").trim();
    if (!want) return;
    const wantLc = want.toLowerCase();
    let match = providerList.find((p) => p.toLowerCase() === wantLc);
    if (!match) match = providerList.find((p) => p.toLowerCase().includes(wantLc) || wantLc.includes(p.toLowerCase()));
    setSelectedProviders([match || want]);
  }, [games, providerList, location.search]);

  const visible = useMemo(() => {
    if (!games) return [];
    // First, narrow to this category (or favourites set).
    let list;
    if (category === "favourite") {
      const favSet = new Set(favourites);
      list = games.filter((g) => favSet.has(g.id));
    } else {
      const cat = String(category || "").toLowerCase();
      const aliases = (TYPE_ALIASES[category] || [category]).map((s) => s.toLowerCase());
      list = games.filter((g) => {
        const cats = (g.categories || []).map((c) => String(c || "").toLowerCase());
        if (cats.includes(cat)) return true;
        return aliases.includes(String(g.type || "").toLowerCase());
      });
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((g) =>
        (g.name || "").toLowerCase().includes(q) ||
        (g.provider || "").toLowerCase().includes(q));
    }
    if (selectedProviders.length > 0) {
      const set = new Set(selectedProviders.map((p) => p.toLowerCase()));
      list = list.filter((g) => set.has(String(g.provider || "").toLowerCase()));
    }
    if (sort === "name") {
      list = list.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sort === "newest") {
      list = list.slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    } else {
      list = list.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return list;
  }, [games, category, favourites, search, sort, selectedProviders]);

  if (!cfg) {
    return (
      <SiteLayout>
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold">Category not found</h1>
        </div>
      </SiteLayout>
    );
  }

  const loading = games === null;
  const empty = !loading && visible.length === 0;

  return (
    <SiteLayout>
      <div className="max-w-7xl lg:max-w-[1600px] mx-auto px-3 sm:px-6 pt-5 sm:pt-8 pb-10">
        {/* Title + toolbar */}
        <div className="flex items-center justify-between gap-3 mb-4 sm:mb-6 relative">
          <button
            type="button"
            onClick={() => setCatMenuOpen((o) => !o)}
            data-testid="category-title-btn"
            className="flex items-center gap-2 sm:gap-3 min-w-0 hover:opacity-90 transition-opacity"
          >
            {category === "favourite" ? (
              <span className="h-10 w-10 sm:h-11 sm:w-11 flex items-center justify-center shrink-0">
                <Heart size={32} fill="#c89b3c" stroke="#c89b3c" strokeWidth={1.5} />
              </span>
            ) : (
              <img
                src={menuIcon(
                  category === "casino" ? "casino" :
                  category === "slots"  ? "slot"   :
                  category === "crash"  ? "crash"  :
                  category === "table"  ? "table"  :
                  category === "fishing"? "fish"   :
                  category === "arcade" ? "arcade" :
                  category === "lottery"? "lottery":
                  category === "sports" ? "sport"  : "logo"
                )}
                alt={cfg.title}
                className="h-10 w-10 sm:h-11 sm:w-11 object-contain shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
              />
            )}
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight truncate">
              {cfg.title}
            </h1>
            {catMenuOpen ? (
              <ChevronUp className="text-white/60 shrink-0" size={20} />
            ) : (
              <ChevronDown className="text-white/45 shrink-0" size={20} />
            )}
          </button>

          <div className="flex items-center gap-1.5">
            <ToolbarIconBtn
              testId="category-search-btn"
              title="Search"
              onClick={() => {
                const el = document.getElementById("cat-search-input");
                if (el) el.focus();
              }}
            >
              <Search size={18} />
            </ToolbarIconBtn>
            <ToolbarIconBtn testId="category-filter-btn" title="Filter" onClick={() => setFilterOpen(true)} active={selectedProviders.length > 0}>
              <SlidersHorizontal size={18} />
              {selectedProviders.length > 0 && (
                <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-[#19a55c] text-white text-[9px] font-bold flex items-center justify-center">
                  {selectedProviders.length}
                </span>
              )}
            </ToolbarIconBtn>
            <SortMenu sort={sort} setSort={setSort} />
          </div>

          {catMenuOpen && (
            <CategoryDropdown
              activeCategory={category}
              onSelect={(to) => { setCatMenuOpen(false); navigate(to); }}
              onClose={() => setCatMenuOpen(false)}
            />
          )}
        </div>

        {/* ── 9-Wickets transfer-wallet notice (Sports only) ───────────────
           Bengali advisory shown only on the Sports category page to
           inform users about how the 9-Wickets transfer wallet works:
           on entry the site balance is moved into the 9-Wickets wallet
           (site shows 0); after returning, the Refresh button pulls the
           balance back within ~10 seconds. */}
        {category === "sports" && (
          <div
            className="mb-5 sm:mb-6 rounded-xl border border-amber-400/30 bg-gradient-to-br from-[#1d1408]/90 via-[#1a1305]/85 to-[#15110a]/90 shadow-[0_4px_24px_-8px_rgba(255,201,51,0.18)] overflow-hidden"
            data-testid="sports-wckt-notice"
            lang="bn"
          >
            <div className="flex items-stretch">
              <div className="hidden sm:flex w-1.5 bg-gradient-to-b from-amber-400/80 via-amber-500/60 to-amber-600/40" aria-hidden="true" />
              <div className="flex-1 p-4 sm:p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="shrink-0 mt-0.5 h-9 w-9 rounded-lg bg-amber-500/15 border border-amber-400/30 flex items-center justify-center">
                    <Info size={18} className="text-amber-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[15px] sm:text-base font-bold text-amber-200 leading-snug">
                      ৯ উইকেট (9 Wickets) — গুরুত্বপূর্ণ নির্দেশনা
                    </div>
                    <div className="text-[12px] sm:text-[13px] text-amber-100/70 mt-0.5">
                      ট্রান্সফার ওয়ালেট সিস্টেম
                    </div>
                  </div>
                </div>

                {/* ── BOLD ALERT: Balance Refresh required before entering ── */}
                <div
                  className="mb-3 rounded-lg border-2 border-amber-400/60 bg-gradient-to-r from-amber-500/15 via-amber-500/20 to-amber-500/15 px-3.5 py-3 flex items-start gap-2.5"
                  data-testid="sports-wckt-refresh-alert"
                >
                  <RefreshCw size={18} className="shrink-0 mt-0.5 text-amber-300" />
                  <div className="text-[13px] sm:text-[14px] leading-relaxed text-amber-100 font-semibold">
                    ⚠️ <span className="text-amber-300 font-bold">9 Wicket</span>-এ প্রবেশ করার জন্য{" "}
                    <span className="text-amber-300 font-bold underline">প্রতিবার</span>{" "}
                    গেমে ঢোকার আগে{" "}
                    <span className="text-amber-300 font-bold">Balance Refresh</span>{" "}
                    করা <span className="text-amber-300 font-bold">অবশ্যই</span>{" "}
                    বাধ্যতামূলক — তারপর ৯ উইকেট গেমে প্রবেশ করতে পারবেন।
                  </div>
                </div>

                <p className="text-[13px] sm:text-[14px] leading-relaxed text-white/85 mb-3">
                  প্রিয় গ্রাহক, ৯ উইকেট একটি{" "}
                  <span className="text-amber-300 font-semibold">ট্রান্সফার ওয়ালেট</span>{" "}
                  ভিত্তিক গেম। আপনি যখন ৯ উইকেট-এ প্রবেশ করবেন, তখন আপনার মূল
                  ব্যালেন্স স্বয়ংক্রিয়ভাবে ৯ উইকেট ওয়ালেটে স্থানান্তরিত হবে এবং
                  আমাদের সাইটে আপনার ব্যালেন্স{" "}
                  <span className="text-amber-300 font-semibold">শূন্য (০)</span>{" "}
                  দেখাবে — এটি স্বাভাবিক প্রক্রিয়া, চিন্তার কোনো কারণ নেই।
                </p>

                <div className="rounded-lg bg-white/5 border border-white/10 p-3 sm:p-3.5 mb-3">
                  <div className="text-[12px] sm:text-[13px] font-semibold text-amber-200/90 mb-2 flex items-center gap-1.5">
                    <ArrowRightLeft size={14} className="text-amber-300" />
                    গেম শেষে ব্যালেন্স ফেরত পাওয়ার নিয়ম
                  </div>
                  <ol className="space-y-1.5 text-[12.5px] sm:text-[13px] text-white/80 leading-relaxed list-none">
                    <li className="flex gap-2">
                      <span className="shrink-0 h-5 w-5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-[11px] font-bold flex items-center justify-center">১</span>
                      <span>৯ উইকেট গেম থেকে বের হয়ে আমাদের সাইটে ফিরে আসুন।</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="shrink-0 h-5 w-5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-[11px] font-bold flex items-center justify-center">২</span>
                      <span>
                        উপরের ব্যালেন্স{" "}
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/15 border border-amber-400/30 text-amber-200 font-semibold">
                          <RefreshCw size={11} /> Refresh
                        </span>{" "}
                        বাটনে ক্লিক করুন।
                      </span>
                    </li>
                    <li className="flex gap-2">
                      <span className="shrink-0 h-5 w-5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-[11px] font-bold flex items-center justify-center">৩</span>
                      <span>
                        মাত্র{" "}
                        <span className="text-amber-300 font-semibold">১০ সেকেন্ডের</span>{" "}
                        মধ্যে আপনার সম্পূর্ণ ব্যালেন্স ৯ উইকেট ওয়ালেট থেকে
                        স্বয়ংক্রিয়ভাবে আমাদের সাইটে ফিরে আসবে।
                      </span>
                    </li>
                  </ol>
                </div>

                <div className="flex items-start gap-2 text-[12px] sm:text-[12.5px] text-emerald-200/85 bg-emerald-500/8 border border-emerald-400/20 rounded-lg px-3 py-2">
                  <ShieldCheck size={15} className="shrink-0 mt-0.5 text-emerald-300" />
                  <span>
                    আপনার ব্যালেন্স{" "}
                    <span className="font-semibold text-emerald-200">সম্পূর্ণ সুরক্ষিত</span>{" "}
                    — কোনো সময় টাকা হারানোর সম্ভাবনা নেই। যেকোনো সমস্যায় ২৪/৭ লাইভ
                    সাপোর্টে যোগাযোগ করুন।
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search pill (always visible, focused via icon) */}
        <div className="mb-4 sm:mb-6">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              id="cat-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${cfg.title.toLowerCase()}...`}
              data-testid="category-search-input"
              className="w-full pl-9 pr-3 h-10 rounded-lg bg-[#161e2a] border border-white/5 focus:border-white/20 focus:outline-none text-sm text-white placeholder:text-white/35"
            />
          </div>
        </div>

        {/* Grid */}
        {loadError && loading ? (
          /* Hard failure on first load — show a friendly retry panel
             instead of the endless skeleton spinner that used to lock the
             page when the network blipped on initial open. */
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center" data-testid="category-load-error">
            <div className="h-14 w-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-4">
              <RefreshCw className="h-6 w-6 text-white/70" />
            </div>
            <div className="text-white font-semibold text-base mb-1">{loadError}</div>
            <div className="text-white/50 text-[13px] mb-5 max-w-sm">
              Your connection looks unstable. We&apos;ll retry automatically when you come back to this tab, or you can tap the button below.
            </div>
            <button
              type="button"
              onClick={retryLoad}
              data-testid="category-load-retry"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-lg text-sm font-semibold text-white transition-transform duration-150 hover:scale-[1.03] active:scale-95"
              style={{ background: cfg.accent || "#19a55c", boxShadow: `0 6px 18px ${cfg.accent || "#19a55c"}55` }}
            >
              <RefreshCw className="h-4 w-4" /> Retry
            </button>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl aspect-[4/5] bg-[#161e2a] border border-white/5 animate-pulse"
              />
            ))}
          </div>
        ) : empty ? (
          <EmptyState accent={cfg.accent} title={cfg.title} category={category} />
        ) : (
          <>
            {loadError && (
              /* Soft refresh-failed banner shown above the existing games so
                 the player knows what they're seeing might be stale. */
              <div className="mb-3 flex items-center justify-between gap-3 rounded-lg px-3 py-2 bg-amber-500/10 border border-amber-400/30 text-amber-200 text-[13px]" data-testid="category-load-warning">
                <span className="truncate">{loadError}</span>
                <button
                  type="button"
                  onClick={retryLoad}
                  data-testid="category-load-retry-soft"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-400/15 hover:bg-amber-400/25 text-amber-100 text-[12px] font-semibold transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Retry
                </button>
              </div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3" data-testid="category-games-grid">
              {visible.slice(0, visibleCount).map((g) => (
                <GameCard
                  key={g.id}
                  game={g}
                  accent={cfg.accent}
                  onLaunch={launchGame}
                  isFavourite={favourites.includes(g.id)}
                  onToggleFavourite={toggleFavourite}
                />
              ))}
            </div>
            <LoadMoreFooter
              shown={Math.min(visibleCount, visible.length)}
              total={visible.length}
              onLoadMore={() => setVisibleCount((n) => n + PAGE_SIZE)}
              accent={cfg.accent}
            />
          </>
        )}
      </div>
      {launchModal}
      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        providers={providerList}
        selected={selectedProviders}
        onApply={(list) => { setSelectedProviders(list); setFilterOpen(false); }}
      />
    </SiteLayout>
  );
}

function ToolbarIconBtn({ children, onClick, title, testId, active = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`relative h-9 w-9 rounded-lg flex items-center justify-center border transition-colors ${
        active
          ? "bg-[#19a55c]/15 border-[#19a55c]/50 text-white"
          : "bg-[#161e2a] border-white/5 hover:border-white/20 text-white/75 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function SortMenu({ sort, setSort }) {
  const [open, setOpen] = useState(false);
  const options = [
    { value: "order",  label: "Default" },
    { value: "name",   label: "A → Z" },
    { value: "newest", label: "Newest" },
  ];
  return (
    <div className="relative">
      <ToolbarIconBtn testId="category-sort-btn" title="Sort" onClick={() => setOpen((o) => !o)}>
        <ArrowDownAZ size={18} />
      </ToolbarIconBtn>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-36 rounded-lg bg-[#1d2735] border border-white/10 shadow-xl z-20 overflow-hidden">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => { setSort(o.value); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                  sort === o.value ? "text-white bg-white/10" : "text-white/75 hover:bg-white/5"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CategoryDropdown({ activeCategory, onSelect, onClose }) {
  return (
    <>
      {/* click-away backdrop */}
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div
        className="absolute left-0 top-full mt-2 w-[300px] sm:w-[340px] rounded-xl bg-[#0f1621] border border-white/10 shadow-2xl z-40 overflow-hidden"
        data-testid="category-dropdown"
      >
        <ul className="py-1 max-h-[70vh] overflow-y-auto">
          {DROPDOWN_ITEMS.map((item) => {
            const isActive = item.slug === activeCategory;
            return (
              <li key={item.slug}>
                <button
                  type="button"
                  onClick={() => onSelect(item.to)}
                  data-testid={`category-dropdown-${item.slug}`}
                  className={`w-full flex items-center gap-4 px-4 py-3 text-left transition-colors ${
                    isActive ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"
                  }`}
                >
                  {item.iconNode ? (
                    <span className="h-9 w-9 flex items-center justify-center shrink-0">
                      {item.iconNode}
                    </span>
                  ) : (
                    <img
                      src={item.icon}
                      alt={item.label}
                      className="h-9 w-9 object-contain shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                    />
                  )}
                  <span className={`text-[15px] font-semibold truncate ${isActive ? "text-white" : "text-white/85"}`}>
                    {item.label}
                  </span>
                  {isActive && (
                    <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[#19a55c]" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}

function FilterModal({ open, onClose, providers, selected, onApply }) {
  const [draft, setDraft] = useState(selected);
  const [providersOpen, setProvidersOpen] = useState(true);

  useEffect(() => { if (open) setDraft(selected); }, [open, selected]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const toggle = (p) => {
    setDraft((arr) => arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p]);
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center sm:p-6 bg-black/75 backdrop-blur-sm"
      onClick={onClose}
      data-testid="category-filter-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full sm:max-w-md bg-[#0f1621] border border-white/8 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <h3 className="text-white text-lg font-bold">Filter</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="h-9 w-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <button
            type="button"
            onClick={() => setProvidersOpen((o) => !o)}
            className="w-full flex items-center justify-between py-2 text-white font-semibold text-[15px]"
            data-testid="filter-providers-toggle"
          >
            <span>Providers</span>
            {providersOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          {providersOpen && (
            <div className="mt-2 space-y-1">
              {providers.length === 0 ? (
                <p className="text-white/45 text-sm py-6 text-center">
                  No providers available in this category yet.
                </p>
              ) : (
                providers.map((p) => {
                  const on = draft.includes(p);
                  return (
                    <label
                      key={p}
                      className="flex items-center gap-3 py-2.5 px-2 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                      data-testid={`filter-provider-${p.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span
                        className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                          on ? "bg-[#19a55c] border-[#19a55c]" : "border-white/25 bg-transparent"
                        }`}
                      >
                        {on && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2.5 6.2 5 8.7l4.5-5.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                      <span className="text-white/85 text-[15px] font-medium">{p}</span>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(p)}
                        className="sr-only"
                      />
                    </label>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="grid grid-cols-2 gap-3 px-5 py-4 border-t border-white/8">
          <button
            type="button"
            onClick={() => setDraft([])}
            data-testid="filter-clear-btn"
            className="h-11 rounded-lg border border-white/15 text-white/85 hover:bg-white/5 font-semibold text-sm transition-colors"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={() => onApply(draft)}
            data-testid="filter-apply-btn"
            className="h-11 rounded-lg bg-[#19a55c] hover:bg-[#21c46e] text-white font-bold text-sm shadow-[0_6px_18px_rgba(25,165,92,0.4)] transition-colors"
          >
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ accent, title, category }) {
  const isFavourite = category === "favourite";
  return (
    <div
      className="rounded-2xl px-6 py-16 flex flex-col items-center justify-center text-center"
      style={{ background: `linear-gradient(135deg, ${accent}14 0%, #0a1119 80%)`, border: "1px solid rgba(255,255,255,0.05)" }}
    >
      <span
        className="h-14 w-14 rounded-full flex items-center justify-center mb-4"
        style={{ background: `${accent}22`, color: accent }}
      >
        {isFavourite ? <Heart size={24} fill="currentColor" /> : <Inbox size={24} />}
      </span>
      <h3 className="text-white text-lg font-semibold">
        {isFavourite ? "No favourites yet" : `No ${title} games yet`}
      </h3>
      <p className="text-white/55 text-sm mt-1.5 max-w-md">
        {isFavourite
          ? "Tap the heart icon on any game tile on the home page to add it to your favourites — they'll show up here."
          : <>Admin can add games to this category from <span className="font-mono text-white/80">/root/admin/games</span>. They will appear here automatically.</>
        }
      </p>
    </div>
  );
}

function LoadMoreFooter({ shown, total, onLoadMore, accent }) {
  const hasMore = shown < total;
  const pct = total > 0 ? Math.min(100, Math.round((shown / total) * 100)) : 0;
  const remaining = Math.max(0, total - shown);
  return (
    <div className="mt-9 sm:mt-12 flex flex-col items-center" data-testid="load-more-footer">
      {hasMore && (
        <button
          type="button"
          onClick={onLoadMore}
          data-testid="load-more-btn"
          aria-label={`Load ${Math.min(40, remaining)} more games`}
          className="group relative inline-flex items-center gap-3 h-14 sm:h-[60px] pl-3 pr-6 sm:pr-7 rounded-full text-white font-bold text-[14px] sm:text-[15px] tracking-wide overflow-hidden transition-transform duration-300 hover:-translate-y-0.5 active:translate-y-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%), #131a26",
            border: "1px solid rgba(255,255,255,0.07)",
            boxShadow:
              "0 1px 0 rgba(255,255,255,0.06) inset, 0 18px 36px -16px rgba(0,0,0,0.7)",
          }}
        >
          {/* Glowing color halo behind button */}
          <span
            aria-hidden
            className="absolute -inset-[2px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none -z-0 blur-md"
            style={{
              background: `conic-gradient(from 0deg, ${accent}00, ${accent}aa, ${accent}00, ${accent}aa, ${accent}00)`,
            }}
          />
          {/* Shine sweep on hover */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full overflow-hidden pointer-events-none"
          >
            <span
              className="absolute -inset-y-2 -left-1/3 w-1/3 opacity-0 group-hover:opacity-100"
              style={{
                background:
                  "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.18) 50%, transparent 70%)",
                animation: "loadMoreShine 1.4s ease-out infinite",
              }}
            />
          </span>

          {/* Left circular icon chip with accent glow */}
          <span
            aria-hidden
            className="relative z-10 inline-flex items-center justify-center h-10 w-10 sm:h-11 sm:w-11 rounded-full shrink-0 transition-transform duration-500 group-hover:rotate-180"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${accent}55, ${accent}15 60%, transparent 80%), #0c121b`,
              boxShadow: `inset 0 0 0 1px ${accent}55, 0 0 22px -4px ${accent}88`,
              color: accent,
            }}
          >
            <ChevronDown size={18} strokeWidth={2.5} />
          </span>

          <span className="relative z-10 inline-flex items-center gap-2">
            <span>Load more</span>
            <span
              className="hidden sm:inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-[11px] font-extrabold tracking-wider"
              style={{
                background: `${accent}22`,
                color: accent,
                boxShadow: `inset 0 0 0 1px ${accent}55`,
              }}
            >
              +{Math.min(40, remaining)}
            </span>
          </span>
        </button>
      )}

      {/* Premium progress bar with shimmer head */}
      <div
        className="mt-6 sm:mt-7 relative w-full max-w-[300px] sm:max-w-[380px] h-1.5 rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.06)" }}
        data-testid="load-more-progress"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full relative transition-[width] duration-700 ease-out"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${accent}aa 0%, ${accent} 100%)`,
            boxShadow: `0 0 14px -2px ${accent}aa`,
          }}
        >
          {/* Pulsing dot at the head of the progress */}
          {hasMore && pct > 2 && (
            <span
              aria-hidden
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 h-2.5 w-2.5 rounded-full"
              style={{
                background: accent,
                boxShadow: `0 0 12px ${accent}, 0 0 0 3px ${accent}33`,
                animation: "loadMorePulse 1.4s ease-in-out infinite",
              }}
            />
          )}
        </div>
      </div>

      {/* Counter with completion badge */}
      <div
        className="mt-4 inline-flex items-center gap-2 text-[12px] sm:text-[13px] tracking-wide"
        data-testid="load-more-counter"
      >
        <span className="text-white/55 font-medium">Shown</span>
        <span className="text-white font-bold tabular-nums">{shown.toLocaleString()}</span>
        <span className="text-white/30 font-medium">·</span>
        <span className="text-white/55 font-medium">of</span>
        <span className="text-white font-bold tabular-nums">{total.toLocaleString()}</span>
        <span className="text-white/55 font-medium">games</span>
        <span
          className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-extrabold tabular-nums"
          style={{
            color: hasMore ? accent : "#19a55c",
            background: hasMore ? `${accent}1a` : "rgba(25,165,92,0.12)",
            boxShadow: `inset 0 0 0 1px ${hasMore ? accent : "#19a55c"}55`,
          }}
        >
          {hasMore ? `${pct}%` : "ALL"}
        </span>
      </div>

      {/* keyframes */}
      <style>{`
        @keyframes loadMoreShine{0%{transform:translateX(-30%)}100%{transform:translateX(900%)}}
        @keyframes loadMorePulse{0%,100%{transform:translate(50%,-50%) scale(1);opacity:1}50%{transform:translate(50%,-50%) scale(1.4);opacity:0.6}}
      `}</style>
    </div>
  );
}
